/**
 * Memory Admin Hooks (Employee-only)
 *
 * React Query hooks for memory administration:
 * - useMemoryAdminParameters: Get all parameter definitions
 * - useMemoryAdminTenantConfig: Get tenant-specific config
 * - useMemoryAdminCostEstimate: Get cost projections
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface ParameterDefinition {
  name: string
  display_name: string
  description: string
  type: string
  default: unknown
  category: string
  min?: number
  max?: number
  allowed_values?: string[]
  sensitivity: string
}

export interface ParameterCategory {
  key: string
  name: string
  description: string
}

export interface CostProjection {
  storage_bytes: number
  storage_mb: number
  embedding_count: number
  embedding_cost_monthly: number
  consolidation_cost_monthly: number
  total_cost_monthly: number
  breakdown: Record<string, number>
}

// =============================================================================
// HOOKS
// =============================================================================

export function useMemoryAdminParameters() {
  return useQuery<{ parameters: ParameterDefinition[]; categories: ParameterCategory[] }>({
    queryKey: ['memory', 'admin', 'parameters'],
    queryFn: async () => {
      const { data } = await api.get('/api/memory/admin/config/parameters')
      return data
    },
  })
}

export function useMemoryAdminTenantConfig(tenantId: string) {
  return useQuery<{ tenant_id: string; config: Record<string, unknown> }>({
    queryKey: ['memory', 'admin', 'tenant', tenantId],
    queryFn: async () => {
      const { data } = await api.get(`/api/memory/admin/config/tenant/${tenantId}`)
      return data
    },
    enabled: !!tenantId,
  })
}

export function useMemoryAdminCostEstimate(episodesPerMonth = 100) {
  return useQuery<CostProjection>({
    queryKey: ['memory', 'admin', 'costs', episodesPerMonth],
    queryFn: async () => {
      const { data } = await api.post('/api/memory/admin/config/cost-estimate', null, {
        params: { episodes_per_month: episodesPerMonth },
      })
      return data
    },
  })
}

export function useSetTenantConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      parameterName,
      value,
    }: {
      tenantId: string
      parameterName: string
      value: unknown
    }) => {
      const { data } = await api.post(`/api/memory/admin/config/tenant/${tenantId}`, {
        parameter_name: parameterName,
        value,
      })
      return data
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['memory', 'admin', 'tenant', vars.tenantId] })
    },
  })
}
